/**
 * LE JEU PARTAGE : UNE SEULE DEFINITION, QUELLE QUE SOIT L'APP QUI LE MONTRE.
 *
 * LE PROBLEME QU'IL FERME (retour du fondateur du 14/10/2026, pile TAC). Un jeu
 * (une famille et son sous-type, `tasks` / `tache`) peut etre montre par
 * plusieurs apps : Projets et Timesheet montrent les memes taches. Sa
 * definition (ses champs, ses listes, ses fichiers) etait pourtant RECOPIEE
 * dans chaque app, et chaque copie vivait sa vie : Parametres > Timesheet >
 * Taches montrait la liste « Service », Parametres > Projets > Taches montrait
 * Etiquettes, Categorie et Validation, sans Service. Deux formulaires pour les
 * memes fiches, deux listes pour le meme champ.
 *
 * LA REGLE : LES LISTES ET LES CHAMPS D'UN JEU APPARTIENNENT AU JEU, pas a
 * l'app ni a la porte. Partout ou un jeu apparait, on lit la meme definition,
 * et un changement fait d'un cote se voit de l'autre. Ce module est la seule
 * lecture de cette regle ; il est PUR (ni Firestore, ni React, ni fichier) et
 * en JavaScript, comme `portee-jeu.mjs`, pour etre lu tel quel par le socle,
 * les scripts de la CI, le Studio du depot et les Functions :
 *
 * - `ecartsDesJeuxPartages` : le garde-fou. Deux apps qui declarent le meme jeu
 *   avec des champs, des listes ou une fiche differents, et le message qui dit
 *   quoi et ou (CI, Studio, `org:check`, `org:publier`).
 * - `jeuxDeLOrg` : ce qu'une org a personnalise d'un jeu, lu dans ses
 *   instances. C'est la definition que TOUTES ses apps montrent.
 * - `alignerSurLesJeux` : une declaration recalee sur ces definitions (le jeu
 *   remplace, ses listes du socle recopiees, ses champs poses sur la fiche des
 *   portes qui le montrent). L'ecran l'applique a la lecture, le serveur a
 *   l'ecriture, le Studio du depot aux fichiers des autres apps.
 *
 * Le detail et les cas limites : `docs/socle/jeu-listes.md`.
 */

import { genreDuType, sansTypeFondu } from './catalogue-des-types.mjs';

const estObjet = (valeur) => valeur !== null && typeof valeur === 'object' && !Array.isArray(valeur);

/** La clef d'un jeu : sa famille et son sous-type, comme les dedoublonne le socle. */
export const cleDuJeu = (jeu) => `${jeu.family}::${jeu.type ?? ''}`;

/** Une valeur JSON, clefs triees : deux definitions egales s'ecrivent pareil. */
export function canonique(valeur) {
  const trier = (v) => Array.isArray(v)
    ? v.map(trier)
    : estObjet(v)
      ? Object.fromEntries(Object.entries(v).filter(([, x]) => x !== undefined).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([k, x]) => [k, trier(x)]))
      : v;
  return JSON.stringify(trier(valeur));
}

const ecransDe = (declaration) => (Array.isArray(declaration?.ecrans?.ecrans) ? declaration.ecrans.ecrans.filter(estObjet) : []);
const jeuxDe = (declaration) => (Array.isArray(declaration?.champs?.jeux) ? declaration.champs.jeux : []);
const portesDe = (ecran) => (Array.isArray(ecran.portes) ? ecran.portes.filter(estObjet) : []);
const visePorte = (porte, cle) => estObjet(porte.jeu) && typeof porte.jeu.family === 'string' && cleDuJeu(porte.jeu) === cle;
const nu = (nom) => nom.replace(/^customFields\./, '');
const estNatif = (nom) => nom.startsWith('socle:');
const A_CHOIX = new Set(['select', 'multiselect']);
/** Les clefs que le jeu MASQUE (`jeux[].masques`) : dans le jeu, sur aucune fiche, hors de Parametres. */
export const masquesDuJeu = (jeu) => new Set(Array.isArray(jeu?.masques) ? jeu.masques.filter((clef) => typeof clef === 'string') : []);

/**
 * LES LISTES DU SOCLE D'UN JEU : les choix d'un champ natif (`socle:status`,
 * `socle:priority`), declares sur les colonnes des ecrans qui montrent le jeu
 * (celles de l'ecran, puis celles de ses portes, qui l'emportent) ET cites par
 * une de ses portes. Le catalogue de colonnes est celui de l'ECRAN, partage par
 * ses jeux : sans la citation, le statut des taches deviendrait une liste des
 * projets. Parametres les regle a cote des listes du jeu ; elles en suivent
 * donc la regle.
 */
function nativesDuJeu(declaration, cle) {
  const natives = {};
  for (const ecran of ecransDe(declaration)) {
    const portes = portesDe(ecran).filter((porte) => visePorte(porte, cle));
    if (portes.length === 0) continue;
    const cites = new Set(portes.flatMap((porte) => [...citesParLaPorte(ecran, porte)]));
    for (const colonne of [...(ecran.colonnes ?? []), ...portes.flatMap((porte) => porte.colonnes ?? [])]) {
      if (estObjet(colonne) && typeof colonne.champ === 'string' && estNatif(colonne.champ) && cites.has(colonne.champ) && A_CHOIX.has(colonne.type)
        && Array.isArray(colonne.options) && colonne.options.length > 0) natives[colonne.champ] = colonne.options;
    }
  }
  return natives;
}

/** Les jeux qu'une declaration porte, par clef : le jeu tel quel et ses listes du socle. */
export function definitionsDeJeux(declaration) {
  const definitions = new Map();
  for (const jeu of jeuxDe(declaration)) {
    if (!estObjet(jeu) || typeof jeu.family !== 'string' || !Array.isArray(jeu.champs)) continue;
    const cle = cleDuJeu(jeu);
    definitions.set(cle, { cle, jeu, natives: nativesDuJeu(declaration, cle) });
  }
  return definitions;
}

/** Ce qui appartient au jeu, hors de ce qui le nomme : champs, fichiers, chatter, couleur, photo, portee, nom. */
const corpsDuJeu = ({ family: _family, type: _type, ...corps }) => corps;

/**
 * DEUX DEFINITIONS EGALES : le meme jeu, et les memes choix pour chaque liste du
 * socle que les DEUX declarent (une app qui ne montre pas le statut n'en
 * declare pas les choix, et ce n'est pas un ecart).
 */
export function memeDefinition(a, b) {
  if (canonique(corpsDuJeu(a.jeu)) !== canonique(corpsDuJeu(b.jeu))) return false;
  return Object.keys(a.natives).every((champ) => b.natives[champ] === undefined || canonique(a.natives[champ]) === canonique(b.natives[champ]));
}

/* Le mot d'un type dans une phrase d'ecart : celui du catalogue, le meme que le journal. */
const genre = (champ) => genreDuType(champ.type);
const motDuChamp = (champ) => `« ${champ.label ?? champ.key} » (${champ.key})`;
const choix = (options) => {
  const mots = (options ?? []).map((option) => (estObjet(option) ? option.label ?? option.value : String(option)));
  return mots.length > 6 ? `${mots.slice(0, 6).join(', ')}...` : mots.join(', ') || 'aucun';
};
/** Ce qui separe deux listes de choix : les choix d'un seul cote, puis ce qui change sur les communs. */
function ecartDeChoix(optionsA, optionsB, nomA, nomB) {
  const valeur = (option) => (estObjet(option) ? option.value : option);
  const mot = (option) => (estObjet(option) ? option.label ?? option.value : String(option));
  const listeA = Array.isArray(optionsA) ? optionsA : [];
  const listeB = Array.isArray(optionsB) ? optionsB : [];
  const valeursA = new Map(listeA.map((option) => [valeur(option), option]));
  const valeursB = new Map(listeB.map((option) => [valeur(option), option]));
  const parties = [];
  const seulsA = listeA.filter((option) => !valeursB.has(valeur(option)));
  const seulsB = listeB.filter((option) => !valeursA.has(valeur(option)));
  if (seulsA.length > 0) parties.push(`${choix(seulsA)} seulement chez ${nomA}`);
  if (seulsB.length > 0) parties.push(`${choix(seulsB)} seulement chez ${nomB}`);
  const changes = listeA.filter((option) => valeursB.has(valeur(option)) && canonique(option) !== canonique(valeursB.get(valeur(option))));
  if (changes.length > 0) parties.push(`${changes.map(mot).join(', ')} : mot, couleur ou état différents`);
  if (parties.length === 0) parties.push(`ordre différent entre ${nomA} et ${nomB}`);
  return `les choix diffèrent (${parties.join(' ; ')})`;
}
const MOTS_DU_JEU = { nom: 'le nom du jeu', fichiers: 'les fichiers joints', chatter: 'le suivi', couleur: 'la couleur des fiches', photo: 'la photo de référence', scope: 'la portée', masques: 'les champs masqués' };

/**
 * CE QUI DIFFERE ENTRE DEUX DEFINITIONS DU MEME JEU, en francais, une ligne par
 * fait : c'est ce que lit celui qui doit corriger, pas un diff JSON.
 */
export function differencesDeJeu(a, b, nomA, nomB) {
  const lignes = [];
  const champsA = new Map(a.jeu.champs.filter(estObjet).map((champ) => [champ.key, champ]));
  const champsB = new Map(b.jeu.champs.filter(estObjet).map((champ) => [champ.key, champ]));
  for (const key of new Set([...champsA.keys(), ...champsB.keys()])) {
    const ca = champsA.get(key);
    const cb = champsB.get(key);
    if (cb === undefined) lignes.push(`${motDuChamp(ca)} : seulement chez ${nomA}`);
    else if (ca === undefined) lignes.push(`${motDuChamp(cb)} : seulement chez ${nomB}`);
    else if (canonique(ca) !== canonique(cb)) {
      if (ca.type !== cb.type) lignes.push(`${motDuChamp(ca)} : ${genre(ca)} chez ${nomA}, ${genre(cb)} chez ${nomB}`);
      else if (canonique(ca.options) !== canonique(cb.options)) lignes.push(`${motDuChamp(ca)} : ${ecartDeChoix(ca.options, cb.options, nomA, nomB)}`);
      else {
        const clefs = [...new Set([...Object.keys(ca), ...Object.keys(cb)])].filter((clef) => canonique(ca[clef]) !== canonique(cb[clef]));
        lignes.push(`${motDuChamp(ca)} : réglages différents (${clefs.join(', ')})`);
      }
    }
  }
  if (lignes.length === 0 && canonique([...champsA.keys()]) !== canonique([...champsB.keys()])) lignes.push(`l'ordre des champs diffère entre ${nomA} et ${nomB}`);
  const corpsA = corpsDuJeu(a.jeu);
  const corpsB = corpsDuJeu(b.jeu);
  for (const clef of new Set([...Object.keys(corpsA), ...Object.keys(corpsB)])) {
    if (clef === 'champs' || canonique(corpsA[clef]) === canonique(corpsB[clef])) continue;
    lignes.push(`${MOTS_DU_JEU[clef] ?? clef} : différent entre ${nomA} et ${nomB}`);
  }
  for (const champ of Object.keys(a.natives)) {
    if (b.natives[champ] === undefined || canonique(a.natives[champ]) === canonique(b.natives[champ])) continue;
    lignes.push(`« ${champ.slice(6)} » (liste du socle) : ${ecartDeChoix(a.natives[champ], b.natives[champ], nomA, nomB)}`);
  }
  return lignes;
}

/* ------------------------------------------------------------------------ */
/* Ce que les portes d'un jeu citent                                         */
/* ------------------------------------------------------------------------ */

const ROLES = ['title', 'subtitle', 'avatar', 'status', 'badges', 'groupKey', 'lane', 'dateStart', 'dateEnd'];

/** Les champs d'une source de role (`{ champ }`, `{ champs }`, `{ replis }`), comme `champsDeSource`. */
function champsDeSource(source) {
  if (typeof source === 'string') return [source];
  if (!estObjet(source)) return [];
  return [
    ...(typeof source.champ === 'string' ? [source.champ] : []),
    ...(Array.isArray(source.champs) ? source.champs.flatMap(champsDeSource) : []),
    ...(Array.isArray(source.replis) ? source.replis.flatMap(champsDeSource) : []),
  ];
}

/** Les champs d'une condition (`{ et }`, `{ ou }`, feuille `{ champ }`), comme `champsDeCondition`. */
function champsDeCondition(condition) {
  if (!estObjet(condition)) return [];
  if (Array.isArray(condition.et)) return condition.et.flatMap(champsDeCondition);
  if (Array.isArray(condition.ou)) return condition.ou.flatMap(champsDeCondition);
  return typeof condition.champ === 'string' ? [condition.champ] : [];
}

/** Les champs qu'une carte de fiche RECLAME (les totaux lies visent l'autre jeu). */
const champsDeCartes = (cartes) => (Array.isArray(cartes) ? cartes : []).flatMap((carte) => (estObjet(carte) && Array.isArray(carte.champs)
  ? carte.champs.flatMap((champ) => (typeof champ === 'string' ? [champ] : estObjet(champ) && !('total' in champ) && typeof champ.champ === 'string' ? [champ.champ] : []))
  : []));

/** Le detail par vue d'une porte : le sien, ou celui de l'ecran (un `vb` de porte remplace celui de l'ecran, sans fusion). */
const bindingsDe = (ecran, porte) => (estObjet(porte.vb) ? porte.vb : estObjet(ecran.vb) ? ecran.vb : {});

/** Tout ce qu'une porte cite de son jeu : vues, cartes, filtres, stats, tranche. Noms nus. */
function citesParLaPorte(ecran, porte) {
  const cites = new Set();
  const bindings = bindingsDe(ecran, porte);
  for (const vue of Array.isArray(porte.vues) ? porte.vues : Array.isArray(ecran.vues) ? ecran.vues : []) {
    const binding = bindings[vue];
    if (!estObjet(binding)) continue;
    for (const nom of Array.isArray(binding.colonnes) ? binding.colonnes : []) if (typeof nom === 'string') cites.add(nu(nom));
    for (const role of ROLES) champsDeSource(binding[role]).forEach((nom) => cites.add(nu(nom)));
    for (const source of Object.values(estObjet(binding.extra) ? binding.extra : {})) champsDeSource(source).forEach((nom) => cites.add(nu(nom)));
    champsDeCartes(binding.cartes).forEach((nom) => cites.add(nu(nom)));
    for (const regle of Array.isArray(binding.surbrillances) ? binding.surbrillances : []) champsDeCondition(regle?.si).forEach((nom) => cites.add(nu(nom)));
  }
  const filtres = estObjet(porte.filtres) ? porte.filtres : {};
  if (typeof filtres.parChamp === 'string') cites.add(nu(filtres.parChamp));
  for (const filtre of Array.isArray(filtres.autres) ? filtres.autres : []) champsDeCondition(filtre?.condition).forEach((nom) => cites.add(nu(nom)));
  for (const stat of Array.isArray(porte.stats) ? porte.stats : []) if (typeof stat?.champ === 'string' && stat.liee === undefined) cites.add(nu(stat.champ));
  champsDeCondition(porte.tranche).forEach((nom) => cites.add(nu(nom)));
  /* LES CHAMPS DE « EN TÊTE DE FICHE » (`etapes`, chantier `fiche-suivi`) : la
     rangee 3 les montre, donc la porte les cite. Sans eux, une autre app qui
     retirait l'un de ces champs du jeu partage passait (`champsCitesHorsDuJeu`),
     et son en-tete disparaissait en silence. */
  for (const enTete of enTetesDeLaPorte(porte)) {
    cites.add(nu(enTete.champ));
    /* LE VERROU D'UN EN-TETE lit la fiche : les champs de sa condition sont cites aussi. */
    for (const verrou of enTete.verrous ?? []) champsDeCondition(verrou.si).forEach((nom) => cites.add(nu(nom)));
  }
  return cites;
}

/**
 * LE VERROU D'UN EN-TETE, lu avec tolerance (chantier `etapes-verrou`,
 * `docs/socle/etapes-verrou.md`) : une condition `si`, une `raison` en
 * quelques mots, et ce qu'il ferme : `avant` (les valeurs de rang inferieur a
 * celle-ci), `depuis` (celle-ci et les suivantes), ou toute la piste sans l'un
 * ni l'autre. `avant` et `depuis` s'excluent : un verrou qui porte les deux n'en
 * est pas un. Un verrou sans condition ou sans raison n'en est pas un non plus :
 * il s'ecarte, le schema le refuse a l'ecriture.
 */
function verrouDe(brut) {
  if (!estObjet(brut) || !estObjet(brut.si) || typeof brut.raison !== 'string' || brut.raison.trim() === '') return undefined;
  const avant = typeof brut.avant === 'string' && brut.avant !== '' ? brut.avant : undefined;
  const depuis = typeof brut.depuis === 'string' && brut.depuis !== '' ? brut.depuis : undefined;
  if (avant !== undefined && depuis !== undefined) return undefined;
  return { si: brut.si, ...(avant === undefined ? {} : { avant }), ...(depuis === undefined ? {} : { depuis }), raison: brut.raison.trim() };
}

/**
 * LES VERROUS D'UN EN-TETE, la LISTE canonique : la clef `verrou` d'un en-tete
 * porte soit un objet seul (l'ancienne forme, lue comme une liste d'un), soit
 * une liste. Un verrou mal forme s'ecarte (`verrouDe`).
 */
function verrousDe(brut) {
  return (Array.isArray(brut) ? brut : brut === undefined ? [] : [brut]).map(verrouDe).filter((verrou) => verrou !== undefined);
}

/**
 * LES VALEURS QUE LES VERROUS D'UN EN-TETE FERMENT, et pourquoi (chantier
 * `etapes-verrou`) : la seule regle, lue par la barre d'etapes et par le
 * kanban. `ordre` est le catalogue ORDONNE du champ (archivees comprises),
 * `courante` la valeur de la fiche (jamais fermee), `actif(verrou)` dit si la
 * condition d'un verrou est vraie sur la fiche.
 *
 * Une valeur est fermee si AU MOINS un verrou actif la ferme ; sa raison est
 * celle du PREMIER verrou (dans l'ordre de la liste) qui la ferme. Un `avant`
 * ou un `depuis` que le catalogue ne connait plus ne ferme rien.
 *
 * @param {readonly { avant?: string, depuis?: string, raison: string }[]} verrous
 * @param {readonly string[]} ordre
 * @param {string | null | undefined} courante
 * @param {(verrou: object) => boolean} actif
 * @returns {Map<string, string>} la valeur fermee et sa raison, dans l'ordre du catalogue
 */
export function valeursFermeesParLesVerrous(verrous, ordre, courante, actif) {
  const fermees = new Map();
  for (const verrou of verrous ?? []) {
    if (!actif(verrou)) continue;
    const seuil = verrou.avant ?? verrou.depuis;
    const rang = seuil === undefined ? 0 : ordre.indexOf(seuil);
    if (rang < 0) continue;
    ordre.forEach((valeur, index) => {
      if (valeur === courante || fermees.has(valeur)) return;
      const fermee = verrou.depuis !== undefined ? index >= rang : verrou.avant !== undefined ? index < rang : true;
      if (fermee) fermees.set(valeur, verrou.raison);
    });
  }
  return new Map(ordre.filter((valeur) => fermees.has(valeur)).map((valeur) => [valeur, fermees.get(valeur)]));
}

/**
 * LES EN-TETES DE FICHE D'UNE PORTE (`etapes`, chantier `fiche-suivi`), dans
 * leur ordre : la LISTE `[{ champ, mode, verrous? }, ...]`, la forme canonique que tout
 * ecrit. L'ancienne forme, un objet `{ champ, mode }` seul, reste LUE et vaut
 * une liste d'un element : aucune declaration deja ecrite ne change de sens.
 * Absent : `[]`, rien dans la rangee 3.
 *
 * LA SEULE LECTURE DE CETTE CLEF : le schema, le montage, la rangee 3 du
 * runtime, le journal, les Parametres et les Studios passent par elle, jamais
 * par `porte.etapes` directement (deux lecteurs divergeraient sur l'ancienne
 * forme). Elle est ICI, dans ce module pur, parce que le serveur et la CI la
 * lisent aussi ; `@taki/data-model` la reexporte.
 *
 * Lecture TOLERANTE, la validation est au schema : une entree sans champ est
 * ignoree, un mode inconnu vaut `flux`, un champ deja vu n'entre pas deux fois,
 * un verrou mal forme s'ecarte (`verrouDe`). LA CLEF DU FICHIER est `verrou`
 * (un objet seul, ou une liste) ; la lecture rend toujours `verrous`, une liste,
 * absente quand elle est vide.
 *
 * @param {unknown} porte une porte de `taki.screens.json`
 * @returns {{ champ: string, mode: 'flux' | 'selection', verrous?: { si: object, avant?: string, depuis?: string, raison: string }[] }[]}
 */
export function enTetesDeLaPorte(porte) {
  const brut = estObjet(porte) ? porte.etapes : undefined;
  const liste = Array.isArray(brut) ? brut : estObjet(brut) ? [brut] : [];
  const vus = new Set();
  const enTetes = [];
  for (const enTete of liste) {
    if (!estObjet(enTete) || typeof enTete.champ !== 'string' || enTete.champ === '' || vus.has(enTete.champ)) continue;
    vus.add(enTete.champ);
    const verrous = verrousDe(enTete.verrou);
    enTetes.push({ champ: enTete.champ, mode: enTete.mode === 'selection' ? 'selection' : 'flux', ...(verrous.length === 0 ? {} : { verrous }) });
  }
  return enTetes;
}

/**
 * LES CHAMPS QU'« EN TÊTE DE FICHE » OFFRE pour un jeu (chantier
 * `fiche-suivi`) : ses champs a choix UNIQUE (`select` avec des valeurs), hors
 * champs masques, puis les natifs dont la colonne du catalogue de l'ecran
 * porte les valeurs. `{ value, label }`, `value` le nom du vocabulaire des
 * colonnes. LA SEULE LECTURE de cette offre : le Studio d'une app lue et les
 * Parametres (`enTete.ts`), le brief (`champsDEtapes`) la lisent ici.
 *
 * @param {unknown} jeu le jeu declare (`taki.fields.json`), ou rien
 * @param {unknown} colonnes le catalogue de colonnes de l'ecran
 * @returns {{ value: string, label: string }[]}
 */
export function champsDEnTeteDuJeu(jeu, colonnes) {
  const masques = masquesDuJeu(jeu);
  const aDesValeurs = (c) => c.type === 'select' && Array.isArray(c.options) && c.options.length > 0;
  return [
    ...(estObjet(jeu) && Array.isArray(jeu.champs) ? jeu.champs : [])
      .filter((c) => estObjet(c) && typeof c.key === 'string' && aDesValeurs(c) && !masques.has(c.key))
      .map((c) => ({ value: c.key, label: typeof c.label === 'string' ? c.label : c.key })),
    ...(Array.isArray(colonnes) ? colonnes : [])
      .filter((c) => estObjet(c) && typeof c.champ === 'string' && estNatif(c.champ) && aDesValeurs(c))
      .map((c) => ({ value: c.champ, label: typeof c.label === 'string' ? c.label : c.champ })),
  ];
}

/**
 * LE CHAMP DE CHAQUE EN-TETE D'UNE PORTE EST UNE LISTE DE SON JEU : les fautes
 * d'une declaration entiere, une phrase par en-tete fautif. Un champ NATIF se juge
 * dans le schema des ecrans (son catalogue vit dans les colonnes) ; un champ
 * DECLARE se juge ici, contre le fichier de champs : il existe dans le jeu de
 * la porte, et c'est une liste a choix unique (un clic pose UNE valeur).
 *
 * POURQUOI ICI (audit I17) : la regle ne vivait qu'au montage du modele
 * (`app-declaree.ts`). Une instance d'org au champ retire, ou devenu multiple,
 * passait le serveur du Studio, et la rangee disparaissait a l'ecran sans un
 * mot. Ce module pur est lu par le montage, par le moteur du Studio
 * (`suiteDuBrouillon`, que le serveur embarque) et par la CI.
 *
 * @param {unknown} declaration `{ champs, ecrans }`
 * @returns {string[]}
 */
export function fautesDesEtapes(declaration) {
  const fautes = [];
  for (const ecran of ecransDe(declaration)) {
    for (const porte of portesDe(ecran)) {
      if (!estObjet(porte.jeu) || typeof porte.jeu.family !== 'string') continue;
      const cle = cleDuJeu(porte.jeu);
      const jeu = jeuxDe(declaration).find((candidat) => estObjet(candidat) && typeof candidat.family === 'string' && cleDuJeu(candidat) === cle);
      /* CHAQUE EN-TETE se juge comme le seul d'avant : la liste n'ajoute aucune regle. */
      const champsDuJeu = estObjet(jeu) && Array.isArray(jeu.champs) ? jeu.champs.filter(estObjet) : [];
      for (const { champ, verrous = [] } of enTetesDeLaPorte(porte)) {
        const declare = estNatif(champ) ? undefined : champsDuJeu.find((c) => c.key === champ);
        if (!estNatif(champ)) {
          if (declare === undefined) fautes.push(`la porte "${porte.id}" montre "${champ}" en tete de fiche, un champ que son jeu ne declare pas.`);
          else if (declare.type !== 'select') fautes.push(`la porte "${porte.id}" montre "${champ}" en tete de fiche : c'est un champ ${declare.type}, pas une liste a choix unique.`);
        }
        /* CHAQUE VERROU : sa valeur « avant » ou « depuis » est une option du champ (un champ natif
           se juge dans le schema des ecrans, son catalogue vit dans les colonnes), et sa condition
           ne cite que des champs du jeu (un natif se juge au montage, contre le schema de sa famille). */
        const options = declare !== undefined && Array.isArray(declare.options) ? declare.options.filter(estObjet) : null;
        for (const verrou of verrous) {
          for (const [mot, valeur] of [['avant', verrou.avant], ['depuis', verrou.depuis]]) {
            if (valeur !== undefined && options !== null && !options.some((o) => o.value === valeur)) {
              fautes.push(`la porte "${porte.id}" verrouille "${champ}" ${mot === 'avant' ? 'avant' : 'a partir de'} "${valeur}", qui n'est pas une valeur de ce champ.`);
            }
          }
          for (const cite of champsDeCondition(verrou.si)) {
            if (!estNatif(cite) && !champsDuJeu.some((c) => c.key === nu(cite))) {
              fautes.push(`la porte "${porte.id}" verrouille "${champ}" sur "${cite}", un champ que son jeu ne declare pas.`);
            }
          }
        }
      }
    }
  }
  return fautes;
}

/**
 * LES CHAMPS QU'UNE PORTE DU JEU CITE ET QUE LE JEU N'A PAS : ce qu'une
 * definition venue d'une autre app casserait ici. Champs natifs exclus.
 */
export function champsCitesHorsDuJeu(declaration, cle) {
  const definition = definitionsDeJeux(declaration).get(cle);
  if (definition === undefined) return [];
  const clefs = new Set(definition.jeu.champs.filter(estObjet).map((champ) => champ.key));
  const fautes = [];
  for (const ecran of ecransDe(declaration)) {
    for (const porte of portesDe(ecran).filter((candidate) => visePorte(candidate, cle))) {
      const manquants = [...citesParLaPorte(ecran, porte)].filter((nom) => !estNatif(nom) && !nom.includes('.') && !clefs.has(nom));
      if (manquants.length > 0) fautes.push({ porte: porte.label ?? porte.id, champs: manquants });
    }
  }
  return fautes;
}

/** La fiche d'une porte : son binding `core.fiche`, et s'il est le sien (et non celui de l'ecran). */
function ficheDe(ecran, porte) {
  if (estObjet(porte.vb)) return { binding: estObjet(porte.vb['core.fiche']) ? porte.vb['core.fiche'] : null, propre: true };
  return { binding: estObjet(ecran.vb) && estObjet(ecran.vb['core.fiche']) ? ecran.vb['core.fiche'] : null, propre: false };
}

/** Ce que la fiche montre : ses roles, et ses cartes (ou, sans cartes, ses colonnes). */
function montresParLaFiche(binding) {
  const montres = new Set();
  for (const role of ROLES) champsDeSource(binding[role]).forEach((nom) => montres.add(nu(nom)));
  const cartes = Array.isArray(binding.cartes) && binding.cartes.length > 0;
  for (const nom of cartes ? champsDeCartes(binding.cartes) : Array.isArray(binding.colonnes) ? binding.colonnes : []) {
    if (typeof nom === 'string') montres.add(nu(nom));
  }
  return montres;
}

/**
 * LES CHAMPS DU JEU QU'UNE FICHE NE MONTRE PAS, porte par porte : la moitie
 * « formulaire » de la regle. Une porte sans fiche (`core.fiche` absent) n'a
 * rien a montrer.
 */
export function champsHorsFiche(declaration, cle) {
  const definition = definitionsDeJeux(declaration).get(cle);
  if (definition === undefined) return [];
  const resultat = [];
  for (const ecran of ecransDe(declaration)) {
    for (const porte of portesDe(ecran).filter((candidate) => visePorte(candidate, cle))) {
      const { binding } = ficheDe(ecran, porte);
      if (binding === null) continue;
      const montres = montresParLaFiche(binding);
      const masques = masquesDuJeu(definition.jeu);
      const absents = definition.jeu.champs.filter((champ) => estObjet(champ) && !masques.has(champ.key) && !montres.has(champ.key));
      if (absents.length > 0) resultat.push({ porte: porte.label ?? porte.id, champs: absents.map((champ) => champ.key), libelles: absents.map(motDuChamp) });
    }
  }
  return resultat;
}

/**
 * LES CHAMPS MASQUES QUI TIENNENT ENCORE UN ROLE (titre, statut, groupe...),
 * porte par porte et vue par vue. En carte ou en colonne, un champ masque
 * reste ecrit et l'affichage le cache (`sansChampsMasques`) ; un role, lui, ne
 * se cache pas sans casser la vue : c'est une faute.
 */
export function champsMasquesMontres(declaration, cle) {
  const definition = definitionsDeJeux(declaration).get(cle);
  if (definition === undefined) return [];
  const masques = masquesDuJeu(definition.jeu);
  if (masques.size === 0) return [];
  const resultat = [];
  for (const ecran of ecransDe(declaration)) {
    for (const porte of portesDe(ecran).filter((candidate) => visePorte(candidate, cle))) {
      const roles = new Set();
      for (const binding of Object.values(bindingsDe(ecran, porte))) {
        if (!estObjet(binding)) continue;
        for (const role of ROLES) champsDeSource(binding[role]).forEach((nom) => roles.add(nu(nom)));
      }
      const montres = [...roles].filter((nom) => masques.has(nom));
      if (montres.length > 0) resultat.push({ porte: porte.label ?? porte.id, champs: montres });
    }
  }
  return resultat;
}

/* ------------------------------------------------------------------------ */
/* Le garde-fou                                                              */
/* ------------------------------------------------------------------------ */

/** Le nom parlant d'un jeu (« Taches »), lu sur sa premiere porte, sinon sur le jeu. */
function nomDuJeu(declarations, cle) {
  for (const declaration of declarations) {
    for (const ecran of ecransDe(declaration)) {
      const porte = portesDe(ecran).find((candidate) => visePorte(candidate, cle));
      if (typeof porte?.jeu?.nom === 'string') return porte.jeu.nom;
    }
    const jeu = definitionsDeJeux(declaration).get(cle)?.jeu;
    if (typeof jeu?.nom === 'string') return jeu.nom;
  }
  return cle.replace('::', '/').replace(/\/$/, '');
}

/**
 * LES ECARTS ENTRE LES APPS QUI MONTRENT LE MEME JEU, et rien d'autre : un jeu
 * qu'une seule app declare n'est l'affaire de personne. Deux sortes d'ecart :
 * une DEFINITION differente (champs, listes, fichiers, listes du socle), et un
 * champ du jeu que la FICHE d'une porte ne montre pas.
 *
 * @param apps `[{ id, declaration }]`, une declaration `{ champs, ecrans }` par app.
 */
export function ecartsDesJeuxPartages(apps) {
  const ordonnees = [...apps].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const porteurs = new Map();
  for (const app of ordonnees) {
    for (const [cle, definition] of definitionsDeJeux(app.declaration)) {
      if (!porteurs.has(cle)) porteurs.set(cle, []);
      porteurs.get(cle).push({ app, definition });
    }
  }
  const ecarts = [];
  for (const [cle, liste] of porteurs) {
    if (liste.length < 2) continue;
    const nom = nomDuJeu(liste.map((porteur) => porteur.app.declaration), cle);
    const [reference, ...autres] = liste;
    for (const autre of autres) {
      const differences = differencesDeJeu(reference.definition, autre.definition, reference.app.id, autre.app.id);
      if (differences.length > 0) ecarts.push({ jeu: cle, nom, apps: [reference.app.id, autre.app.id], differences });
    }
    for (const { app } of liste) {
      for (const hors of champsHorsFiche(app.declaration, cle)) {
        ecarts.push({ jeu: cle, nom, apps: [app.id], differences: [`la fiche de la porte « ${hors.porte} » ne montre pas ${hors.libelles.join(', ')}`] });
      }
    }
  }
  /* UN CHAMP MASQUE NE TIENT AUCUN ROLE, que le jeu soit partage ou non. */
  for (const app of ordonnees) {
    for (const cle of definitionsDeJeux(app.declaration).keys()) {
      for (const faute of champsMasquesMontres(app.declaration, cle)) {
        ecarts.push({ jeu: cle, nom: nomDuJeu([app.declaration], cle), apps: [app.id], differences: [`la porte « ${faute.porte} » fait de ${faute.champs.join(', ')} un titre, un statut ou un groupe, alors que le jeu le masque`] });
      }
    }
  }
  return ecarts;
}

/** Le message d'un ecart, tel que la CI, le Studio et `org:publier` l'affichent. */
export function messageDEcart(ecart, consigne = "Corrigez une app, puis recopiez sa définition dans les autres (`pnpm jeux:aligner <app>`), ou passez par le Studio, qui le fait seul.") {
  const qui = ecart.apps.length > 1 ? `partagé par ${ecart.apps.join(' et ')}` : `montré par ${ecart.apps[0]} et une autre app`;
  return [
    `Le jeu « ${ecart.nom} » (${ecart.jeu.replace('::', '/').replace(/\/$/, '')}) est ${qui}, et ses listes ou ses champs diffèrent :`,
    ...ecart.differences.map((ligne) => `  - ${ligne}`),
    `Un jeu appartient à l'org, pas à l'app : ses listes et ses champs sont les mêmes partout (docs/socle/jeu-listes.md). ${consigne}`,
  ].join('\n');
}

/* ------------------------------------------------------------------------ */
/* Ce que l'org a personnalise                                               */
/* ------------------------------------------------------------------------ */

const instant = (date) => (date instanceof Date ? date.getTime() : typeof date?.toMillis === 'function' ? date.toMillis() : Number.NEGATIVE_INFINITY);

/**
 * LES JEUX QUE L'ORG A PERSONNALISES, lus dans ses instances : la definition
 * que toutes ses apps montrent, celles qui tournent sur leur modele comprises.
 *
 * UN JEU QU'UNE INSTANCE N'A PAS TOUCHE N'EN SORT PAS : egal a celui du cliche
 * de son modele (`modele`), il n'est que la copie d'un modele, peut-etre ancien,
 * et il ne doit pas l'emporter sur le modele courant d'une autre app.
 *
 * DEUX INSTANCES EN DESACCORD (une base d'avant la regle) : la plus recente
 * l'emporte (`updatedAt`), a egalite l'id d'app le plus petit. L'ecriture les
 * remet d'accord (`docs/socle/jeu-listes.md`).
 *
 * @param instances une Map ou un objet par id d'app, ou une liste de `{ appId }` :
 *   `{ champs, ecrans, modele?, updatedAt? }`.
 */
export function jeuxDeLOrg(instances) {
  const liste = instances instanceof Map
    ? [...instances.entries()]
    : Array.isArray(instances)
      ? instances.filter(estObjet).map((instance) => [String(instance.appId), instance])
      : Object.entries(estObjet(instances) ? instances : {});
  liste.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  const retenus = new Map();
  for (const [appId, instance] of liste) {
    if (!estObjet(instance)) continue;
    /* Lues en base telles quelles (le serveur) : un type fondu s'y relit dans
       sa forme nouvelle, comme a toute lecture (`sansTypeFondu`). */
    const relue = (declaration) => ({ ...declaration, champs: sansTypeFondu(declaration.champs) });
    const cliche = estObjet(instance.modele) ? definitionsDeJeux(relue(instance.modele)) : new Map();
    const date = instant(instance.updatedAt);
    for (const [cle, definition] of definitionsDeJeux(relue(instance))) {
      const base = cliche.get(cle);
      if (base !== undefined && memeDefinition(base, definition)) continue;
      const deja = retenus.get(cle);
      if (deja !== undefined && !(date > deja.date)) continue;
      retenus.set(cle, { definition: { ...definition, source: appId }, date });
    }
  }
  return new Map([...retenus].map(([cle, { definition }]) => [cle, definition]));
}

/* ------------------------------------------------------------------------ */
/* L'alignement                                                              */
/* ------------------------------------------------------------------------ */

/**
 * UN DETAIL DE VUE SANS LES CHAMPS QUE `garde` REFUSE. Une carte de champs que
 * le filtre VIDE disparait, et une liste videe (`colonnes`, `cartes`) aussi : le
 * schema en exige au moins un element, et une carte sans champ ne se montre
 * pas. Les cartes liees, de fichiers et les totaux ne sont pas des champs du
 * jeu : elles restent. Rend le meme objet quand rien ne change.
 */
function bindingFiltre(binding, garde) {
  let suivant = binding;
  const gardeChamp = (champ) => typeof champ !== 'string' || garde(champ);
  if (Array.isArray(binding.colonnes) && !binding.colonnes.every(gardeChamp)) {
    const colonnes = binding.colonnes.filter(gardeChamp);
    suivant = { ...suivant, colonnes };
    if (colonnes.length === 0) delete suivant.colonnes;
  }
  if (Array.isArray(binding.cartes) && binding.cartes.some((carte) => estObjet(carte) && Array.isArray(carte.champs) && !carte.champs.every(gardeChamp))) {
    const cartes = binding.cartes
      .map((carte) => (estObjet(carte) && Array.isArray(carte.champs) ? { ...carte, champs: carte.champs.filter(gardeChamp) } : carte))
      .filter((carte) => !(estObjet(carte) && Array.isArray(carte.champs) && carte.champs.length === 0));
    suivant = { ...suivant, cartes };
    if (cartes.length === 0) delete suivant.cartes;
  }
  return suivant;
}

/**
 * Ce que le filtre garde d'un jeu : les natifs, les chemins, les champs que le
 * jeu porte, et (a l'AFFICHAGE seulement, `retirerMasques`) pas ceux qu'il masque.
 */
const gardeDuJeu = (clefs, jeu, retirerMasques) => {
  const masques = retirerMasques ? masquesDuJeu(jeu) : new Set();
  return (nom) => typeof nom !== 'string' || estNatif(nom) || nom.includes('.') || (clefs.has(nu(nom)) && !masques.has(nu(nom)));
};

/**
 * Une fiche propre a la porte, recalee sur les champs du jeu : retire ce qui
 * n'existe plus, pose ce qui manque. UN CHAMP MASQUE NE S'Y POSE JAMAIS, et il
 * n'en est PAS retire : la carte qui le range le garde, et le montre de nouveau
 * quand on le demasque. C'est l'affichage qui le cache (`sansChampsMasques`).
 */
function ficheAlignee(binding, clefs, jeu, retirerMasques) {
  const masques = masquesDuJeu(jeu);
  let suivant = bindingFiltre(binding, gardeDuJeu(clefs, jeu, retirerMasques));
  const montres = montresParLaFiche(suivant);
  const absents = jeu.champs.filter((champ) => estObjet(champ) && !masques.has(champ.key) && !montres.has(champ.key)).map((champ) => champ.key);
  if (absents.length === 0) return suivant;
  /* LE CHAMP QUI MANQUE SE POSE dans la derniere carte de champs, comme le
     Studio pose un champ neuf ; sans carte, la fiche montre son pool, ou il
     suffit de l'ajouter aux colonnes. */
  const colonnes = Array.isArray(suivant.colonnes) ? suivant.colonnes : [];
  suivant = { ...suivant, colonnes: [...colonnes, ...absents.filter((key) => !colonnes.some((nom) => typeof nom === 'string' && nu(nom) === key))] };
  if (Array.isArray(suivant.cartes) && suivant.cartes.length > 0) {
    const rang = suivant.cartes.map((carte) => estObjet(carte) && Array.isArray(carte.champs)).lastIndexOf(true);
    suivant = {
      ...suivant,
      cartes: rang === -1
        ? [...suivant.cartes, { titre: 'Informations', champs: absents }]
        : suivant.cartes.map((carte, index) => (index === rang ? { ...carte, champs: [...carte.champs, ...absents] } : carte)),
    };
  }
  return suivant;
}

/** Les vues d'une porte, sans les champs que le jeu n'a plus (et, a l'affichage, sans ceux qu'il masque). */
function vuesAlignees(vb, clefs, jeu, { poser = true, retirerMasques = false } = {}) {
  let change = false;
  const suivant = {};
  const garde = gardeDuJeu(clefs, jeu, retirerMasques);
  for (const [vue, binding] of Object.entries(vb)) {
    let aligne = binding;
    if (estObjet(binding)) aligne = vue === 'core.fiche' && poser ? ficheAlignee(binding, clefs, jeu, retirerMasques) : bindingFiltre(binding, garde);
    if (aligne !== binding) change = true;
    suivant[vue] = aligne;
  }
  return change ? suivant : vb;
}

function ecranAligne(ecran, jeux, finals, { poser = true, retirerMasques = false } = {}) {
  let suivant = ecran;
  const montres = new Set(portesDe(ecran).filter((porte) => estObjet(porte.jeu)).map((porte) => cleDuJeu(porte.jeu)));
  /* LES LISTES DU SOCLE : les choix de l'org, la ou cet ecran les declare deja. */
  const natives = {};
  for (const cle of montres) Object.assign(natives, jeux.get(cle)?.natives ?? {});
  const recaler = (colonnes) => {
    if (!Array.isArray(colonnes)) return colonnes;
    let change = false;
    const suite = colonnes.map((colonne) => {
      if (!estObjet(colonne) || natives[colonne.champ] === undefined || !Array.isArray(colonne.options)) return colonne;
      if (canonique(colonne.options) === canonique(natives[colonne.champ])) return colonne;
      change = true;
      return { ...colonne, options: natives[colonne.champ] };
    });
    return change ? suite : colonnes;
  };
  const colonnesEcran = recaler(ecran.colonnes);
  if (colonnesEcran !== ecran.colonnes) suivant = { ...suivant, colonnes: colonnesEcran };
  /* LES PORTES DU JEU, quand elles ont leur propre detail par vue : un `vb`
     d'ecran sert aussi les portes des autres jeux, on ne le touche pas. */
  let portesChangees = false;
  const portes = (Array.isArray(ecran.portes) ? ecran.portes : []).map((porte) => {
    if (!estObjet(porte)) return porte;
    let aligne = porte;
    const colonnes = recaler(porte.colonnes);
    if (colonnes !== porte.colonnes) aligne = { ...aligne, colonnes };
    const cle = estObjet(porte.jeu) ? cleDuJeu(porte.jeu) : null;
    const jeu = cle !== null && jeux.has(cle) ? finals.get(cle) : undefined;
    if (jeu !== undefined && estObjet(porte.vb)) {
      const vb = vuesAlignees(porte.vb, new Set(jeu.champs.filter(estObjet).map((champ) => champ.key)), jeu, { poser, retirerMasques });
      if (vb !== porte.vb) aligne = { ...aligne, vb };
    }
    if (aligne !== porte) portesChangees = true;
    return aligne;
  });
  if (portesChangees) suivant = { ...suivant, portes };
  /* LE DETAIL D'ECRAN, servi aux portes sans detail propre : A L'AFFICHAGE, quand
     elles montrent toutes UN SEUL jeu recale, ses champs masques en sortent
     aussi (la fiche des projets de l'app Projets). On n'y pose jamais rien. */
  const sansDetail = portesDe(suivant).filter((porte) => !estObjet(porte.vb) && estObjet(porte.jeu));
  const clesSansDetail = new Set(sansDetail.map((porte) => cleDuJeu(porte.jeu)));
  if (retirerMasques && estObjet(suivant.vb) && clesSansDetail.size === 1) {
    const [cle] = clesSansDetail;
    const jeu = jeux.has(cle) ? finals.get(cle) : undefined;
    if (jeu !== undefined && masquesDuJeu(jeu).size > 0) {
      const vb = vuesAlignees(suivant.vb, new Set(jeu.champs.filter(estObjet).map((champ) => champ.key)), jeu, { poser: false, retirerMasques: true });
      if (vb !== suivant.vb) suivant = { ...suivant, vb };
    }
  }
  /* LE CATALOGUE DE COLONNES DE L'ECRAN nomme chaque champ que ses vues citent :
     un champ pose sur une fiche y entre aussi. */
  if (Array.isArray(suivant.colonnes)) {
    const connus = new Set(suivant.colonnes.filter(estObjet).map((colonne) => colonne.champ));
    const ajouts = [];
    for (const porte of portesDe(suivant)) {
      const cle = estObjet(porte.jeu) ? cleDuJeu(porte.jeu) : null;
      if (cle === null || !jeux.has(cle) || !estObjet(porte.vb)) continue;
      for (const champ of finals.get(cle)?.champs ?? []) {
        if (estObjet(champ) && !connus.has(champ.key) && citesParLaPorte(suivant, porte).has(champ.key)) { connus.add(champ.key); ajouts.push({ champ: champ.key }); }
      }
    }
    if (ajouts.length > 0) suivant = { ...suivant, colonnes: [...suivant.colonnes, ...ajouts] };
  }
  return suivant;
}

/**
 * UNE DECLARATION RECALEE SUR DES DEFINITIONS DE JEUX : chaque jeu qu'elle
 * declare et que `jeux` connait est REMPLACE par cette definition, ses listes
 * du socle recopiees la ou l'ecran les declare, et la fiche de chaque porte du
 * jeu (quand elle a son propre detail par vue) montre ses champs : ceux qui
 * manquent s'y posent, ceux qui n'existent plus s'en retirent.
 *
 * REND LA MEME DECLARATION (meme objet) QUAND RIEN NE CHANGE : l'ecran memoise
 * sur cette identite. Ne mute jamais son entree.
 */
export function alignerSurLesJeux(declaration, jeux, { poser = true, retirerMasques = false } = {}) {
  if (!(jeux instanceof Map) || jeux.size === 0 || !estObjet(declaration)) return declaration;
  const propres = jeuxDe(declaration);
  if (!propres.some((jeu) => estObjet(jeu) && typeof jeu.family === 'string' && jeux.has(cleDuJeu(jeu)))) return declaration;
  let change = false;
  const finals = new Map();
  const nouveaux = propres.map((jeu) => {
    if (!estObjet(jeu) || typeof jeu.family !== 'string') return jeu;
    const cle = cleDuJeu(jeu);
    const definition = jeux.get(cle);
    if (definition === undefined) return jeu;
    const remplace = { ...definition.jeu, family: jeu.family, ...(jeu.type === undefined ? {} : { type: jeu.type }) };
    if (remplace.type === undefined) delete remplace.type;
    const garde = canonique(remplace) === canonique(jeu) ? jeu : remplace;
    if (garde !== jeu) change = true;
    finals.set(cle, garde);
    return garde;
  });
  const ecrans = ecransDe(declaration);
  const alignes = (Array.isArray(declaration.ecrans?.ecrans) ? declaration.ecrans.ecrans : []).map((ecran) => (estObjet(ecran) ? ecranAligne(ecran, jeux, finals, { poser, retirerMasques }) : ecran));
  const ecransChanges = alignes.some((ecran, index) => ecran !== declaration.ecrans.ecrans[index]);
  if (!change && !ecransChanges) return declaration;
  return {
    ...declaration,
    ...(change ? { champs: { ...declaration.champs, jeux: nouveaux } } : {}),
    ...(ecransChanges && ecrans.length > 0 ? { ecrans: { ...declaration.ecrans, ecrans: alignes } } : {}),
  };
}

/**
 * LA DECLARATION TELLE QU'ELLE S'AFFICHE : chaque fiche et chaque vue d'une
 * porte du jeu cesse de citer ce que le jeu masque (`jeux[].masques`), une
 * carte dont tous les champs sont masques disparait, et rien n'est pose.
 *
 * POUR L'AFFICHAGE SEULEMENT (l'ecran, `resoudreDeclaration` ; les lectures du
 * serveur, `declarationEffective`), JAMAIS pour ce qui s'enregistre : la
 * declaration ecrite garde ses cartes et leurs champs, et un champ demasque
 * revient a sa place, dans sa carte.
 */
export function sansChampsMasques(declaration) {
  const jeux = new Map([...definitionsDeJeux(declaration)].filter(([, definition]) => masquesDuJeu(definition.jeu).size > 0));
  return alignerSurLesJeux(declaration, jeux, { poser: false, retirerMasques: true });
}

/**
 * LES ECARTS D'UNE ORG : ses instances entre elles, et chaque app qui tourne
 * sur son modele face aux jeux que l'org a personnalises (un champ que son
 * ecran cite et que la definition de l'org retire la casserait).
 *
 * @param modeles `[{ id, declaration }]`, les modeles des apps de l'org.
 * @param instances `[{ appId, declaration, modele? }]`, ses instances.
 */
export function ecartsDeLOrg(modeles, instances) {
  const ecarts = ecartsDesJeuxPartages(instances.map((instance) => ({ id: instance.appId, declaration: instance.declaration })));
  const jeux = jeuxDeLOrg(instances.map((instance) => ({ appId: instance.appId, ...instance.declaration, modele: instance.modele })));
  const avecInstance = new Set(instances.map((instance) => instance.appId));
  for (const modele of modeles) {
    if (avecInstance.has(modele.id)) continue;
    const aligne = alignerSurLesJeux(modele.declaration, jeux);
    if (aligne === modele.declaration) continue;
    for (const cle of definitionsDeJeux(aligne).keys()) {
      if (!jeux.has(cle)) continue;
      for (const faute of champsCitesHorsDuJeu(aligne, cle)) {
        ecarts.push({
          jeu: cle,
          nom: nomDuJeu([aligne], cle),
          apps: [jeux.get(cle).source, modele.id],
          differences: [`la porte « ${faute.porte} » de ${modele.id} cite ${faute.champs.join(', ')}, que la définition de ${jeux.get(cle).source} n'a pas`],
        });
      }
    }
  }
  return ecarts;
}
